/**
 * gridHelpers.js - Utility functions for grid manipulation
 */

/**
 * Creates a 2D array filled with a default value
 * @param {number} size - The size of the grid (size x size)
 * @param {*} [defaultValue=''] - The default value to fill the grid with
 * @returns {Array<Array<*>>} A 2D array of the specified size filled with the default value
 */
export function createEmptyGrid(size, defaultValue = '') { // Export function
  // Ensure size is a positive integer
  const gridSize = Math.max(1, Math.floor(size));
  return Array(gridSize).fill(null).map(() => Array(gridSize).fill(defaultValue));
}

/**
 * Deep clones a 2D grid
 * @param {Array<Array<*>>} grid - The grid to clone
 * @returns {Array<Array<*>>} A deep copy of the grid
 */
export function cloneGrid(grid) { // Export function
  if (!Array.isArray(grid)) return [];
  return grid.map(row => (Array.isArray(row) ? [...row] : []));
}

/**
 * Gets a word from a grid row
 * @param {Array<Array<string>>} grid - The grid to extract from
 * @param {number} rowIndex - The row index
 * @returns {string} The word from the specified row, or empty string if invalid
 */
export function getRowWord(grid, rowIndex) { // Export function
  if (!grid || !grid[rowIndex] || !Array.isArray(grid[rowIndex])) return '';
  return grid[rowIndex].join('');
}

/**
 * Gets a word from a grid column
 * @param {Array<Array<string>>} grid - The grid to extract from
 * @param {number} colIndex - The column index
 * @returns {string} The word from the specified column, or empty string if invalid
 */
export function getColumnWord(grid, colIndex) { // Export function
  if (!grid || grid.length === 0 || colIndex < 0) return '';
  let columnWord = '';
  const gridSize = grid.length;
  for (let row = 0; row < gridSize; row++) {
    // Ensure row exists and has enough columns
    if (!grid[row] || colIndex >= grid[row].length) return ''; // Invalid column access
    columnWord += grid[row][colIndex];
  }
  return columnWord;
}

/**
 * Swaps two cells in a grid
 * @param {Array<Array<*>>} grid - The grid to modify
 * @param {number} row1 - First cell row
 * @param {number} col1 - First cell column
 * @param {number} row2 - Second cell row
 * @param {number} col2 - Second cell column
 * @returns {boolean} True if the swap was performed, false if invalid input or same cell
 */
export function swapCells(grid, row1, col1, row2, col2) { // Export function
  const gridSize = grid?.length;
  if (!gridSize ||
      row1 < 0 || row1 >= gridSize || col1 < 0 || col1 >= gridSize ||
      row2 < 0 || row2 >= gridSize || col2 < 0 || col2 >= gridSize) {
    console.warn("Swap failed: Invalid coordinates or grid.");
    return false;
  }
  if (row1 === row2 && col1 === col2) {
    return false; // Same cell, no swap needed
  }

  // Swap the letters
  [grid[row1][col1], grid[row2][col2]] = [grid[row2][col2], grid[row1][col1]];

  return true;
}

/**
 * Counts occurrences of each letter in a grid
 * @param {Array<Array<string>>} grid - The grid to analyze
 * @returns {Map<string, number>} A map of each letter to its count
 */
export function countGridLetters(grid) { // Export function
  const letterCounts = new Map();
  if (!Array.isArray(grid)) return letterCounts;

  for (let row = 0; row < grid.length; row++) {
    if (!Array.isArray(grid[row])) continue;
    for (let col = 0; col < grid[row].length; col++) {
      const letter = grid[row][col];
      if (typeof letter === 'string' && letter.length === 1) { // Only count single letters
        letterCounts.set(letter, (letterCounts.get(letter) || 0) + 1);
      }
    }
  }
  return letterCounts;
}

/**
 * Checks if two grids have the same letter at a specific position
 * @param {Array<Array<*>>} grid1 - First grid
 * @param {Array<Array<*>>} grid2 - Second grid
 * @param {number} row - Row to check
 * @param {number} col - Column to check
 * @returns {boolean} True if the letters match, false otherwise (including invalid access)
 */
export function cellsMatch(grid1, grid2, row, col) { // Export function
  // Add boundary checks
  if (!grid1 || !grid2 || !grid1[row] || !grid2[row] ||
      col < 0 || col >= grid1[row].length || col >= grid2[row].length) {
    return false;
  }
  return grid1[row][col] === grid2[row][col];
}
